import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Add,
  Description,
  Edit,
  Error as ErrorIcon,
  MoreVert,
  NoteAdd,
} from '@material-ui/icons';
import {
  Button,
  FileUpload,
  FileUploadArea,
  StatusChip,
  StatusDiv,
  StatusType,
  UploadedDocument,
} from '@roc/ui';
import { AddEditLienModal } from './addEditLienModal';
import { EditTaxModal } from './editTaxModal';
import { FieldReviewTable } from '@roc/feature-ai-docs-review';
import { useStore } from '@roc/client-portal-shared/hooks';
import { insertIf } from '@roc/feature-utils';
import { formatCurrency } from '@roc/ui/utils';
import { TaxRecordRow } from '../types';

export interface TitleCommitmentContentProps {
  taskId: number;
  onClose: () => void;
}

const TAX_COLUMNS: {
  key: keyof TaxRecordRow;
  label: string;
  currency?: boolean;
}[] = [
  { key: 'propertyAddress', label: 'Property Address' },
  { key: 'annualTaxAmount', label: 'Annual Tax Amount', currency: true },
  { key: 'taxFrequency', label: 'Tax Frequency' },
  { key: 'taxAmountByFrequency', label: 'Tax Amount by Frequency', currency: true },
];

const useStyles = makeStyles(theme => ({
  dropzoneLabel: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 15,
  },
  addButton: {
    textTransform: 'none',
    fontWeight: 600,
  },
  tableContainer: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeadCell: {
    color: theme.palette.text.secondary,
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    backgroundColor: '#FAFAFA',
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
  },
  emptyStateCell: {
    border: 'none',
    padding: theme.spacing(6, 2),
  },
  emptyStateBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyIcon: {
    color: '#C8C8C8',
    fontSize: 48,
    marginBottom: theme.spacing(1.5),
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  actionButton: {
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    padding: theme.spacing(0.75),
    marginLeft: theme.spacing(1),
    backgroundColor: '#fff',
  },
  flagCell: {
    textAlign: 'center',
    width: 48,
  },
}));

const emptyTableText: Record<string, string> = {
  IN_PROGRESS:
    'Processing document — lien data will appear here once complete.',
  SUCCESS: 'No records found.',
  FAILED: 'Processing failed. Upload a new document to try again.',
};

export const TitleCommitmentContent = observer(
  ({ taskId, onClose }: TitleCommitmentContentProps) => {
    const classes = useStyles();

    const renderStatus = (status: string | null) => {
      if (!status) return '--';
      return (
        <StatusChip
          statusType={StatusType.LIEN_TASK_STATUS}
          label={status}
          size="medium"
          variant="filled"
        />
      );
    };

    const [addLienOpen, setAddLienOpen] = useState(false);
    const [editingLienId, setEditingLienId] = useState<number | null>(null);
    const [items, setItems] = useState<FileUpload[]>([]);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [menuLienIndex, setMenuLienIndex] = useState<number | null>(null);
    const [editTaxOpen, setEditTaxOpen] = useState(false);
    const [editingTaxIndex, setEditingTaxIndex] = useState<number | null>(null);
    const [taxMenuAnchor, setTaxMenuAnchor] = useState<HTMLElement | null>(null);
    const [taxMenuIndex, setTaxMenuIndex] = useState<number | null>(null);

    const openMenu = (
      event: React.MouseEvent<HTMLButtonElement>,
      index: number
    ) => {
      setMenuAnchor(event.currentTarget);
      setMenuLienIndex(index);
    };

    const closeMenu = () => {
      setMenuAnchor(null);
      setMenuLienIndex(null);
    };

    const openTaxMenu = (
      event: React.MouseEvent<HTMLButtonElement>,
      index: number
    ) => {
      setTaxMenuAnchor(event.currentTarget);
      setTaxMenuIndex(index);
    };

    const closeTaxMenu = () => {
      setTaxMenuAnchor(null);
      setTaxMenuIndex(null);
    };

    const { titleCommitmentStore, documentStore, globalStore, docReviewStoreV2 } = useStore();
    const { liens, document, taxRecords, fieldReviewItems, loanProperties } = titleCommitmentStore;
    const showPropertyColumn = loanProperties.length > 0;
    const isMultiProperty = loanProperties.length > 1;
    const COLUMNS = [
      'Lien Type',
      'Amount',
      ...insertIf(showPropertyColumn, ['Property Address']),
      'Lender',
      'Borrower',
      'Dated',
      'Status',
      ...insertIf(globalStore.isInternalPortal, ['Actions']),
    ];

    const uploadLocked = docReviewStoreV2.isUploadLocked(document);

    // A single-property loan's liens all belong to its one property. On a multi-property loan it's
    // blank (not '--') when the lien isn't mapped to a collateral yet - the extracted address
    // couldn't be matched to any of the loan's properties and needs a manual assignment.
    const getPropertyAddress = (propertyId?: number | null) =>
      propertyId != null
        ? loanProperties.find(p => p.propertyId === propertyId)?.fullAddress ?? ''
        : !isMultiProperty
          ? loanProperties[0]?.fullAddress ?? ''
          : '';
    const unassignedLienCount = isMultiProperty
      ? liens.filter(lien => lien.propertyId == null).length
      : 0;

    const formatTaxValue = (
      column: (typeof TAX_COLUMNS)[number],
      record: TaxRecordRow
    ) => {
      const value = record[column.key];
      if (value == null || value === '') return '--';
      return column.currency ? formatCurrency(Number(value)) : String(value);
    };

    useEffect(() => {
      if (!taskId) return;
      titleCommitmentStore.getReviewTaskData(taskId);
    }, [taskId]);

    const handleUploadComplete = () => {
      setItems([]);
      titleCommitmentStore.refetchReviewTaskData(taskId);
    };

    const handleSubmit = items => {
      if (items[0]) {
        documentStore.uploadDocument(
          items[0].file,
          document,
          null,
          handleUploadComplete
        );
        return;
      }
      globalStore.notificationStore.showWarningNotification({
        message: 'Upload a file for submission',
      });
    };

    return (
      <Grid item xs={12} style={{ padding: 16 }}>
        {document?.dropboxPath ? (
          <UploadedDocument
            document={document}
            disabled={titleCommitmentStore.processingStatus === 'IN_PROGRESS'}
            onUpload={
              uploadLocked
                ? undefined
                : file => {
                    documentStore.uploadDocument(
                      file,
                      document,
                      null,
                      handleUploadComplete
                    );
                  }
            }
          />
        ) : (
          !uploadLocked && (
            <>
              <Typography className={classes.dropzoneLabel}>
                Drop title commitment documents here, or click to browse
              </Typography>
              <FileUploadArea
                items={items}
                setItems={updater => {
                  const resolvedItems =
                    typeof updater === 'function' ? updater(items) : updater;
                  setItems(resolvedItems);
                  handleSubmit(resolvedItems);
                }}
              />
            </>
          )
        )}
        {document?.dropboxPath && titleCommitmentStore.processingStatus && (
          <Box mt={1}>
            <StatusDiv
              status={titleCommitmentStore.processingStatus}
              text={titleCommitmentStore.statusText}
              onRefresh={() => titleCommitmentStore.getReviewTaskData(taskId)}
            />
          </Box>
        )}

        <Divider className={classes.divider} />

        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>Property taxes</Typography>
        </Box>

        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeadCell} />
                {TAX_COLUMNS.map(col => (
                  <TableCell key={col.key} className={classes.tableHeadCell}>
                    {col.label}
                  </TableCell>
                ))}
                {globalStore.isInternalPortal && (
                  <TableCell className={classes.tableHeadCell}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {taxRecords.length > 0 ? (
                taxRecords.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell className={classes.flagCell}>
                      {record.addressMatched === false && (
                        <Tooltip title="Address could not be matched to a loan property">
                          <ErrorIcon color="error" />
                        </Tooltip>
                      )}
                    </TableCell>
                    {TAX_COLUMNS.map(col => (
                      <TableCell key={col.key}>
                        {formatTaxValue(col, record)}
                      </TableCell>
                    ))}
                    {globalStore.isInternalPortal && (
                      <TableCell>
                        <IconButton
                          size="medium"
                          className={classes.actionButton}
                          disabled={titleCommitmentStore.processingStatus === 'IN_PROGRESS'}
                          onClick={e => openTaxMenu(e, index)}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={taxMenuIndex === index ? taxMenuAnchor : null}
                          open={taxMenuIndex === index && Boolean(taxMenuAnchor)}
                          onClose={closeTaxMenu}
                          getContentAnchorEl={null}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          <MenuItem
                            onClick={() => {
                              titleCommitmentStore.loadTaxForm(
                                record,
                                index,
                                taxIndex => {
                                  setEditingTaxIndex(taxIndex);
                                  setEditTaxOpen(true);
                                }
                              );
                              closeTaxMenu();
                            }}
                          >
                            <ListItemIcon>
                              <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Edit" />
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={
                      TAX_COLUMNS.length +
                      1 +
                      (globalStore.isInternalPortal ? 1 : 0)
                    }
                    className={classes.emptyStateCell}
                  >
                    <Box className={classes.emptyStateBox}>
                      <Description className={classes.emptyIcon} />
                      <Typography className={classes.emptyText}>
                        No property tax records found yet.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider className={classes.divider} />

        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            Liens on this property
          </Typography>
          {globalStore.isInternalPortal && (
            <Button
              className={classes.addButton}
              variant="contained"
              color="primary"
              size="small"
              disabled={titleCommitmentStore.processingStatus == 'IN_PROGRESS'}
              startIcon={<Add />}
              onClick={() => {
                titleCommitmentStore.lienFormStore.reset();
                setAddLienOpen(true);
              }}
              testId="add-lien-button"
            >
              Add Lien
            </Button>
          )}
        </Box>

        {unassignedLienCount > 0 && (
          <Box mb={2}>
            <StatusDiv
              status="FAILED"
              text={`${unassignedLienCount} lien(s) could not be matched to a property by address. ${
                globalStore.isInternalPortal
                  ? 'Use Edit to assign the property address and save.'
                  : ''
              }`}
            />
          </Box>
        )}
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell key={col} className={classes.tableHeadCell}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {liens.length > 0 ? (
                liens.map((lien, index) => (
                  <TableRow key={index}>
                    <TableCell>{lien.lienType ?? '--'}</TableCell>
                    <TableCell>
                      {lien.originalAmount != null
                        ? formatCurrency(lien.originalAmount)
                        : '--'}
                    </TableCell>
                    {showPropertyColumn && (
                      <TableCell>
                        {getPropertyAddress(lien.propertyId)}
                      </TableCell>
                    )}
                    <TableCell>{lien.madeTo ?? '--'}</TableCell>
                    <TableCell>{lien.madeBy ?? '--'}</TableCell>
                    <TableCell>{lien.datedDate ?? '--'}</TableCell>
                    <TableCell>{renderStatus(lien.status)}</TableCell>
                    {globalStore.isInternalPortal && (
                      <TableCell>
                        <IconButton
                          size="medium"
                          className={classes.actionButton}
                          disabled={titleCommitmentStore.processingStatus === 'IN_PROGRESS'}
                          onClick={e => openMenu(e, index)}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={menuLienIndex === index ? menuAnchor : null}
                          open={menuLienIndex === index && Boolean(menuAnchor)}
                          onClose={closeMenu}
                          getContentAnchorEl={null}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              titleCommitmentStore.loadForm(
                                lien.propertyLienId,
                                lienId => {
                                  setEditingLienId(lienId ?? null);
                                  setAddLienOpen(true);
                                }
                              );
                              closeMenu();
                            }}
                          >
                            <ListItemIcon>
                              <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Edit" />
                          </MenuItem>
                          {!lien.status && (
                            <MenuItem
                              onClick={async () => {
                                await titleCommitmentStore.createResolution(
                                  lien.propertyLienId
                                );
                                closeMenu();
                                onClose();
                              }}
                            >
                              <ListItemIcon>
                                <NoteAdd fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="Create Resolution" />
                            </MenuItem>
                          )}
                        </Menu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    className={classes.emptyStateCell}
                  >
                    <Box className={classes.emptyStateBox}>
                      <Description className={classes.emptyIcon} />
                      <Typography className={classes.emptyText}>
                        {emptyTableText[
                          titleCommitmentStore.processingStatus
                        ] ??
                          'No records found yet. Upload a title commitment document above to get started.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {globalStore.isInternalPortal && (
          <>
            <Divider className={classes.divider} />

            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>Title Review</Typography>
            </Box>

            <TableContainer className={classes.tableContainer}>
              {fieldReviewItems.length > 0 ? (
                <FieldReviewTable items={fieldReviewItems} />
              ) : (
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className={classes.emptyStateCell}>
                        <Box className={classes.emptyStateBox}>
                          <Description className={classes.emptyIcon} />
                          <Typography className={classes.emptyText}>
                            No title review fields found yet.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </>
        )}

        <AddEditLienModal
          open={addLienOpen}
          editingLienId={editingLienId}
          onClose={() => {
            setAddLienOpen(false);
            setEditingLienId(null);
          }}
        />

        <EditTaxModal
          open={editTaxOpen}
          editingTaxIndex={editingTaxIndex}
          onClose={() => {
            setEditTaxOpen(false);
            setEditingTaxIndex(null);
          }}
        />
      </Grid>
    );
  }
);
